import clsx from 'clsx'
import map from 'lodash/map'
import noop from 'lodash/noop'
import React, { Fragment, memo, MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgAngleLeft from '@Assets/SVG/AngleLeft'
import SvgAngleRight from '@Assets/SVG/AngleRight'
import SvgBurger from '@Assets/SVG/Burger'
import SvgClose from '@Assets/SVG/Close'
import { LanguageToggle } from '@Components/Header/LanguageToggle'
import { NATIVE_MODAL_OPEN_CLASS } from '@Components/Modal/NativeModal/NativeModal'
import { ClubSignUpBanner } from '@Domains/Club/Components/ClubSignupBanner'
import { MenuElement } from '@Domains/Navigation/Features/Menu/Components/MenuElement/MenuElement'
import { MenuFooter } from '@Domains/Navigation/Features/Menu/Components/MenuFooter/MenuFooter'
import { BlockTypeWithGlobal, GlobalNavigationQuery, PagePresentation, PageType } from '@Generated/graphql'
import { enableBodyScroll, preventBodyScroll } from '@Globals/BodyHelpers'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { GlobalAnchorItemInjections } from '@Globals/Injections/Client'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import styles from './Menu.module.scss'

export type MenuItem = NonNullable<NonNullable<GlobalNavigationQuery['navigation']>[0]>
export type MenuItemChild = NonNullable<NonNullable<NonNullable<GlobalNavigationQuery['navigation']>[0]>['items']>[0]
export const getMenuItemTitle = (menuItem: MenuItem) => menuItem?.title || (menuItem?.link?.references?.find(r => r && 'title' in r) as undefined | {
  title?: string
})?.title || ''
const BreakPoint = 1024
export const isEventDesktop = (event: MouseEvent<HTMLElement>) => (event.nativeEvent.view ? event.nativeEvent.view.innerWidth > BreakPoint : false)
export const isBodyDesktop = () => (typeof document !== 'undefined' && document.body.clientWidth > BreakPoint)

type MenuProps = {
  items: MenuItem[]
}

export interface MainMenuActionConfig {
  checkDesktop?: boolean
  isClickEvent?: boolean
  isRootCategory?: boolean
  viewAllElement?: boolean
}

const AvailableLocalesLanguageToggle = () => {
  const { availableLocales } = useContext(MetadataContext)
  const { t } = useTranslation()

  if (!availableLocales || availableLocales.length <= 1) {
    return null
  }

  return (
    <div className={styles.language}>
      <p>{t('header:SIDE_BAR_LANG_SELECT_TITLE')}</p>
      <LanguageToggle showTitle={false} />
    </div>
  )
}

type MenuAction = (
  event: MouseEvent<HTMLElement>,
  item: MenuItem,
  config?: MainMenuActionConfig) => void
export const Menu = memo(({ items = [] }: MenuProps) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { t, tDanger } = useTranslation('header')
  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState<Array<MenuItem | MenuItemChild>>([])
  const selectedItem = useMemo(() => history[history.length - 1], [history])
  const selectedItemComponents = useBlockComponents(
    selectedItem && 'blocks' in selectedItem ? selectedItem.blocks as BlockTypeWithGlobal[] : [],
    undefined,
    undefined,
    PagePresentation.Navigation,
    undefined,
  )
  const openModal = useCMSModalOpen()
  const getPageType = useCallback((item: MenuItem | MenuItemChild) => {
    const page = item.link?.references?.find(r => r.__typename === 'Page')
    const pageType = page && 'type' in page ? page.type : 'categories'
    const pageTypeId = page && 'id' in page ? page.id : item.id
    if (pageType === 'categories') {
      return {
        pageType: 'category',
        pageTypeId,
      }
    }
    return {
      pageType,
      pageTypeId,
    }
  }, [])

  const triggerMenu: MenuAction = useCallback((
    event: MouseEvent<HTMLElement>,
    item: MenuItem,
    config?: MainMenuActionConfig,
  ) => {
    const desktop = isEventDesktop(event)

    if (config?.checkDesktop && !desktop) {
      return
    }
    if (config?.checkDesktop && config.isRootCategory && desktop && item.items?.length === 0) {
      setHistory([])
    }
    const {
      pageType,
      pageTypeId,
    } = getPageType(item)
    if (desktop && !config?.isRootCategory && history.length === 1) {
      if (config?.isClickEvent) {
        ampli.navLinkClick({
          source_id: desktop ? 'mega_menu' : 'mobile_menu',
          dest_page_type: pageType,
          dest_page_type_id: pageTypeId,
        })
      }
      return
    }
    if (config?.isRootCategory && desktop && config?.isClickEvent) {
      return
    }
    if (item.items?.length && !config?.viewAllElement) {
      if (desktop) {
        ampli.navDesktopMegaMenuOpen({
          menu_index: item.id,
        })
      } else if (config?.isRootCategory) {
        ampli.navMobileMenuThemeToggle({
          source_id: item.id,
        })
      } else {
        ampli.navMobileMenuCategoryToggle({
          category_id: ((item.link?.references || []).find(a => a.__typename === 'Page') as any)?.id || 'unknown page type',
          is_opened: true,
        })
      }
      event.preventDefault()
      setHistory(config?.isRootCategory ? [item] : [...history, item])
      return
    }
    ampli.navLinkClick({
      source_id: desktop ? 'mega_menu' : 'mobile_menu',
      dest_page_type: pageType,
      dest_page_type_id: pageTypeId,
    })
  }, [getPageType, history])

  const resetOnLeave = useCallback((event?: MouseEvent<HTMLDivElement | HTMLAnchorElement>) => {
    if (!event || isEventDesktop(event)) {
      setOpen(false)
      setHistory([])
      enableBodyScroll()
      return
    }
  }, [])

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > BreakPoint) {
        setOpen(false)
        setHistory([])
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize)
    }
    return () => document.removeEventListener('resize', onResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const createMenuElement = useCallback((menuItem: MenuItemChild, hide: boolean) => {
    if (!menuItem) {
      return null
    }
    const directChild = !!selectedItem?.id && selectedItem?.items?.find(c => c.id === menuItem.id)
    const childItem = history.some(h => h.items?.find(c => c.id === menuItem.id))
    const discoverMore = menuItem.features?.includes('discover_more')
    return (
      <div
        key={directChild ? `directChild_${menuItem.id}` : `child_${menuItem.id}`}
        className={clsx(hide && styles.hide, discoverMore && styles.discoverMore)}
      >
        <MenuElement
          className={clsx(
            styles.menuItem,
            childItem && styles.child,
            directChild && styles.directChild,
            discoverMore && styles.discoverMoreElement,
            selectedItem?.id === menuItem.id && styles.viewAll,
          )}
          menuItem={menuItem}
          shown={!hide}
          triggerMenu={triggerMenu}
          viewAllElement={selectedItem?.id === menuItem.id}
        />
        {menuItem.items && (
          <span>
            {menuItem.items.map(child => createMenuElement(child, hide))}
          </span>
        )}
      </div>
    )
  }, [history, selectedItem?.items, selectedItem?.id, triggerMenu])

  return (
    <nav>
      <div
        className={clsx(styles.menu)}
        onMouseLeave={resetOnLeave}
        ref={ref}
      >
        {(history.length || open) && (
          <div
            className={clsx(styles.backdrop, 'backdrop')}
            onClick={() => resetOnLeave()}
          />
        )}

        <button
          aria-label={t('header:HEADER_MOBILE_MENU_LABEL')}
          className={clsx(styles.burgerButton, open && styles.open, styles.withClub, open && NATIVE_MODAL_OPEN_CLASS)}
          onClick={() => {
            ampli.navMobileMenuToggle({
              is_opened: true,
            })
            preventBodyScroll(true)
            setOpen(true)
          }}
          type="button"
        >
          <SvgBurger />
        </button>
        <div className={clsx(styles.mainMenu, open && styles.open, 'modal')}>
          <div className={styles.mainHeader} />
          <button
            className={styles.close}
            onClick={() => {
              setHistory([])
              setOpen(false)
              enableBodyScroll()
            }}
            type="button"
          >
            {' '}
            <SvgClose />
          </button>

          <AvailableLocalesLanguageToggle />

          {
            items?.map(menuItem => (
              <MenuElement
                key={menuItem.id}
                className={clsx(
                  styles.menuItem,
                  menuItem.id === selectedItem?.id && styles.selectedRootItem,
                  styles.rootItem,
                  menuItem.id === 'OUTLET' && styles.red,
                )}
                isRootCategory
                menuItem={menuItem}
                shown={open}
                triggerMenu={triggerMenu}
              />
            ))
          }
          {open && (
            <div className={styles.rootMenuHero}>
              <GlobalAnchorItemInjections anchor="menu.mobile.footer" />
            </div>
          )}
          <MenuFooter onContactClick={() => openModal(PageType.Modal, 'contact-us')} />
        </div>
        <div
          className={clsx(
            styles.subMenu,
            'modal',
            !!selectedItem && styles.open,
            open && styles.opens,
            styles.subMenuClub,
          )}
          onMouseLeave={e => resetOnLeave(e)}
        >

          <div className={styles.subMenuGrid}>
            <button
              className={styles.close}
              onClick={() => {
                setHistory([])
                setOpen(false)
                enableBodyScroll()
              }}
              type="button"
            >
              {' '}
              <SvgClose />
            </button>
            <div className={styles.subHeader}>
              <button
                className={styles.back}
                onClick={() => setHistory(prev => prev.slice(0, -1))}
                type="button"
              >
                <SvgAngleLeft />
                <p className={styles.prevTitle}>{history[history.length - 2] ? getMenuItemTitle(history[history.length - 2]) : 'menu'}</p>
              </button>
            </div>
            {map(items, item => (
              <Fragment key={`subMenu_${item.id}`}>
                <MenuElement
                  className={clsx(
                    styles.viewAll,
                    styles.menuItem,
                    item.id !== selectedItem?.id && styles.hide,
                  )}
                  menuItem={{
                    ...item,
                    id: `${item.id}_view_all`,
                  }}
                  shown={selectedItem?.id === item.id}
                  triggerMenu={noop}
                  viewAllElement={selectedItem?.id === item.id}
                />
                {item?.items?.map(child => createMenuElement(child, !(child.id === selectedItem?.id || item.id === selectedItem?.id)))}
              </Fragment>
            ))}
          </div>
          <div className={styles.subMenuHero}>
            {selectedItemComponents}
          </div>

          {selectedItem?.link?.url && (
            <a className={styles.subMenuLink} href={selectedItem?.link?.url}>
              <SvgAngleRight />
              {t('HEADER_MOBILE_MENU_VIEW_ALL_LABEL')}
            </a>
          )}
          {!!selectedItem && (
            <ClubSignUpBanner className={styles.clubSignup}>
              <p {...tDanger('common:TRENDHIMCLUB_MAIN_CTA_1_DYNAMIC', { percentage: '15' })} />
            </ClubSignUpBanner>
          )}
        </div>
      </div>
    </nav>
  )
})

Menu.displayName = 'Menu'
